import { useMemo } from 'react';
import PropTypes from 'prop-types';
import './FallingLotusPhoto.css';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

// 產生連續新月形飄落弧線的 SVG path。用真正的拋物線公式 y = a*x^2 + c 取樣多個點，
// 再串成折線點位（點數夠多時，路徑本身已經非常平滑，不需要仰賴貝茲控制點去湊形狀）。
// 拋物線本身的曲率是連續漸變的（頂點曲率最大、越往兩端越平緩），比起貝茲曲線湊出來的
// 「中段平直＋端點急彎」形狀，拋物線整條路徑的曲率變化更均勻，offset-distance
// 等弧長前進時視覺速度感也會更一致；range 加大、拉長水平跨距同樣能讓曲率變得更平緩。
function buildDriftPath(range, lift, segments = 40) {
    const half = range / 2;
    // CSS/SVG 座標系 y 軸向下為正。這裡讓 y 在端點(x=±half)為 +lift（視覺上偏下）、
    // 中間(x=0)為 0（視覺上偏上），即開口向下的拱橋形狀：中間拱起、兩端下垂。
    const a = lift / (half * half);

    const parabolaY = (x) => lift - a * x * x;

    const points = [];
    // 前半段：左端點(-half) 走到右端點(+half)
    for (let i = 0; i <= segments; i++) {
        const x = -half + (range * i) / segments;
        points.push([x, parabolaY(x)]);
    }
    // 後半段：右端點走回左端點（同一條拋物線，原路折返，形成新月形往復）
    for (let i = 1; i <= segments; i++) {
        const x = half - (range * i) / segments;
        points.push([x, parabolaY(x)]);
    }

    const d = `M ${points.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(' L ')} Z`;

    return `path('${d}')`;
}

/**
 * 飄落單張照片特效 - 使用寫實 3D 渲染圖片素材（蓮花／菩提葉共用）
 * Falling single photo effect - using a realistic 3D-rendered image asset
 *
 * 固定從畫面正中央飄落一張圖片，下墜速度統一且偏慢。左右飄動軌跡是連續的拋物線
 * 弧線（真實落葉飄落的樣子）：從一側滑降到另一側再原路折返，形成左右往復的新月形
 * 路徑，而不是原地左右擺盪。swayRange 控制左右總跨距（越大弧線拉得越長）、
 * lift 控制拋物線深度（中間下垂高度，數值越小整條弧線越扁平平滑）。
 * swayAngle 控制圖片本體左右搖晃角度（像鐘擺一樣旋轉，預設 ±15 度），跟位移共用
 * 同一個週期（duration/delay），兩者節奏同步。
 * spin 控制圖片本體是否額外持續 360 度翻轉：蓮花是完整的花朵照片，翻轉會顯得
 * 不自然，預設不轉；菩提葉本身是薄片狀，翻轉飄落才符合真實落葉的樣子，可傳 spin 開啟。
 */
const FallingLotusPhoto = ({
    imageSrc,
    count = 1,
    size = 160,
    duration = 26,
    swayRange = 340,
    swayAngle = 15,
    lift = 110,
    spin = false,
    className = '',
}) => {
    const driftPath = useMemo(() => buildDriftPath(swayRange, lift), [swayRange, lift]);

    const petals = useMemo(() => {
        // 固定從畫面正中央開始飄落，driftPath（新月形飄落弧線）負責左右位移路徑、
        // swayAngle 負責本體晃動角度。drift 與 sway 共用同一個 duration/delay，
        // 讓位移路徑與旋轉搖擺節奏完全同步
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            left: 50,
            delay: randomBetween(0, duration),
            swayDuration: randomBetween(10, 13),
            rotateDuration: randomBetween(6, 10),
            spinDirection: Math.random() > 0.5 ? 1 : -1,
            opacity: randomBetween(0.9, 1),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, duration]);

    return (
        <div className={`falling-lotus-photo-root ${className}`} aria-hidden="true">
            {petals.map((p) => (
                <div
                    key={p.id}
                    className="falling-lotus-photo-fall"
                    style={{
                        left: `${p.left}%`,
                        animationDuration: `${duration}s`,
                        animationDelay: `${-p.delay}s`,
                    }}
                >
                    <div
                        className="falling-lotus-photo-drift"
                        style={{
                            '--drift-path': driftPath,
                            animationDuration: `${p.swayDuration}s`,
                            animationDelay: `${-p.delay}s`,
                        }}
                    >
                        <div
                            className="falling-lotus-photo-sway"
                            style={{
                                '--sway-angle': `${swayAngle}deg`,
                                animationDuration: `${p.swayDuration}s`,
                                animationDelay: `${-p.delay}s`,
                            }}
                        >
                            <div
                                className={spin ? 'falling-lotus-photo-spin' : undefined}
                                style={spin ? {
                                    animationDuration: `${p.rotateDuration}s`,
                                    animationDirection: p.spinDirection === 1 ? 'normal' : 'reverse',
                                    animationDelay: `${-p.delay}s`,
                                } : undefined}
                            >
                                <img
                                    src={imageSrc}
                                    alt=""
                                    width={size}
                                    height={size}
                                    draggable={false}
                                    style={{ opacity: p.opacity, display: 'block' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

FallingLotusPhoto.propTypes = {
    imageSrc: PropTypes.string.isRequired,
    count: PropTypes.number,
    size: PropTypes.number,
    duration: PropTypes.number,
    swayRange: PropTypes.number,
    swayAngle: PropTypes.number,
    lift: PropTypes.number,
    spin: PropTypes.bool,
    className: PropTypes.string,
};

export default FallingLotusPhoto;
